import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { RoutePath } from '../enums/api.enum';
import { RouteGuardService } from "../services/route-guard.service";

/**
 * AuthGuard function to protect routes based on user authentication status.
 *
 * @param {ActivatedRouteSnapshot} route - The activated route snapshot.
 * @param {RouterStateSnapshot} state - The router state snapshot.
 * @returns {boolean} - Returns true if the user is authenticated and allowed to access the route, otherwise false.
 *
 * This guard uses the MSAL (Microsoft Authentication Library) service to check if the user is authenticated.
 * - If the user is authenticated and trying to access the root path, they are redirected to the session path.
 * - If the user is not authenticated, they are redirected to the sign-in path.
 */
export const authGuard: CanActivateFn = (route, state) => {
  const msalService = inject(MsalService);
  const router = inject(Router);
  const account = msalService.instance.getAllAccounts()[0];

  if (account) {
    // User is authenticated
    if (state.url === `/${RoutePath.Root}` || state.url === '/') {
      router.navigate([`/${RoutePath.Session}`]);
      return false;
    }
    return true;
  } else {
    // User is not authenticated
    router.navigate([`/${RoutePath.SignIn}`]);
    return false;
  }
};

export const apiGuard: CanActivateFn = (route, state) => {
  const routeGuardService = inject(RouteGuardService);
  const url = document.URL;
  const path = new URL(url).pathname;

  routeGuardService.loadedUrl = path.substring(1);
  return true; // allows all requests
}

